import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    TextField,
    Grid,
    Autocomplete,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    InputAdornment,
    IconButton,
    Tooltip,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from "@mui/material";
import { Search, Phone, Email } from "@mui/icons-material";
import { collection, query, where } from "firebase/firestore";
import { useFirestore, useFirestoreCollectionData } from "reactfire";
import MainLayout from "../../layouts/main_layout/main_layout";

interface Props { }

const bloodGroups = [
    { id: 1, value: "A+" },
    { id: 2, value: "A-" },
    { id: 3, value: "B+" },
    { id: 4, value: "B-" },
    { id: 5, value: "AB+" },
    { id: 6, value: "AB-" },
    { id: 7, value: "O+" },
    { id: 8, value: "O-" },
];

const cityOptions = [
    { id: 1, value: "Kundapur" },
    { id: 2, value: "Udupi" },
    { id: 3, value: "Shirva" },
    { id: 4, value: "Karkala" },
    { id: 5, value: "Kallianpur" },
];

const genderOptions = [
    { id: 1, value: "Male" },
    { id: 2, value: "Female" },
];

function Donors(props: Props) {
    const firestore = useFirestore();

    // Filter states
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCity, setSelectedCity] = useState<{ id: number; value: string } | null>(null);
    const [selectedBloodGroup, setSelectedBloodGroup] = useState<{ id: number; value: string } | null>(null);
    const [selectedGender, setSelectedGender] = useState<{ id: number; value: string } | null>(null);

    // Pagination states
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Contact dialog state
    const [contactDialogOpen, setContactDialogOpen] = useState(false);
    const [selectedDonor, setSelectedDonor] = useState<any>(null);

    // Fetch all donors from Firebase
    const donorsCollection = collection(firestore, "redonDonors");
    const donorsQuery = query(donorsCollection);
    const { data: donors } = useFirestoreCollectionData(donorsQuery);

    const calculateDaysLeft = (lastDonation: string, gender: string) => {
        if (!lastDonation) return 0;

        const lastDonationDate = new Date(lastDonation);
        const today = new Date();
        const daysPassed = Math.floor((today.getTime() - lastDonationDate.getTime()) / (1000 * 60 * 60 * 24));

        const waitingPeriod = gender === "Male" ? 60 : 90;
        const daysLeft = waitingPeriod - daysPassed;

        return daysLeft > 0 ? daysLeft : 0;
    };

    // Filter donors
    const filteredDonors = donors?.filter((donor: any) => {
        // Only show available donors (daysLeft === 0)
        const daysLeft = calculateDaysLeft(donor.lastDonationDate, donor.gender?.value);
        if (daysLeft !== 0) return false;

        // Search filter
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = !searchTerm ||
            donor.fullName?.toLowerCase().includes(searchLower) ||
            donor.mobileNumber?.includes(searchTerm) ||
            donor.bloodGroup?.value?.toLowerCase().includes(searchLower);

        // City filter
        const matchesCity = !selectedCity || donor.city?.value === selectedCity.value;

        // Blood group filter
        const matchesBloodGroup = !selectedBloodGroup || donor.bloodGroup?.value === selectedBloodGroup.value;

        // Gender filter
        const matchesGender = !selectedGender || donor.gender?.value === selectedGender.value;

        return matchesSearch && matchesCity && matchesBloodGroup && matchesGender;
    }) || [];

    // Pagination handlers
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Paginated donors
    const paginatedDonors = filteredDonors.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const handleRowClick = (donor: any) => {
        setSelectedDonor(donor);
        setContactDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setContactDialogOpen(false);
        setSelectedDonor(null);
    };

    const handlePhoneClick = (mobileNumber: string) => {
        window.location.href = `tel:${mobileNumber}`;
    };

    const handleEmailClick = (email: string) => {
        window.location.href = `mailto:${email}`;
    };

    return (
        <MainLayout>
            <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh', py: 4 }}>
                <Container maxWidth="lg">
                    <Box mb={4}>
                        <Typography variant="h4" fontWeight={700} gutterBottom>
                            Blood Donors
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Find available blood donors in your area
                        </Typography>
                    </Box>

                    {/* Search Bar */}
                    <Box mb={3}>
                        <TextField
                            fullWidth
                            placeholder="Search by name, mobile number, or blood group..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ backgroundColor: '#fff' }}
                        />
                    </Box>

                    {/* Filters */}
                    <Box
                        sx={{
                            p: 3,
                            backgroundColor: '#fff',
                            borderRadius: 2,
                            mb: 3,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
                        }}
                    >
                        <Typography variant="h6" fontWeight={600} mb={2}>
                            Filters
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item md={3} sm={6} xs={12}>
                                <Typography variant="subtitle2" mb={1}>
                                    City
                                </Typography>
                                <Autocomplete
                                    fullWidth
                                    value={selectedCity}
                                    onChange={(e, value) => setSelectedCity(value)}
                                    options={cityOptions}
                                    getOptionLabel={(option) => option.value}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="All Cities" size="small" />
                                    )}
                                />
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <Typography variant="subtitle2" mb={1}>
                                    Blood Group
                                </Typography>
                                <Autocomplete
                                    fullWidth
                                    value={selectedBloodGroup}
                                    onChange={(e, value) => setSelectedBloodGroup(value)}
                                    options={bloodGroups}
                                    getOptionLabel={(option) => option.value}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="All Blood Groups" size="small" />
                                    )}
                                />
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <Typography variant="subtitle2" mb={1}>
                                    Gender
                                </Typography>
                                <Autocomplete
                                    fullWidth
                                    value={selectedGender}
                                    onChange={(e, value) => setSelectedGender(value)}
                                    options={genderOptions}
                                    getOptionLabel={(option) => option.value}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="All Genders" size="small" />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Donors List */}
                    <Box
                        sx={{
                            backgroundColor: '#fff',
                            borderRadius: 2,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
                        }}
                    >
                        <Box p={3} pb={2}>
                            <Typography variant="h6" fontWeight={600}>
                                Available Donors ({filteredDonors.length})
                            </Typography>
                        </Box>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Sl. No.</strong></TableCell>
                                        <TableCell><strong>Full Name</strong></TableCell>
                                        <TableCell><strong>City</strong></TableCell>
                                        <TableCell><strong>Blood Group</strong></TableCell>
                                        <TableCell><strong>Last Donation</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                        <TableCell><strong>Contact</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedDonors.length > 0 ? (
                                        paginatedDonors.map((donor: any, index: number) => (
                                            <TableRow
                                                key={donor.NO_ID_FIELD}
                                                hover
                                                onClick={() => handleRowClick(donor)}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                                <TableCell>{donor.fullName}</TableCell>
                                                <TableCell>{donor.city?.value}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={donor.bloodGroup?.value}
                                                        color="error"
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {donor.lastDonationDate || "Never"}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label="Available"
                                                        color="success"
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box display="flex" gap={1}>
                                                        <Tooltip title="Call">
                                                            <IconButton
                                                                color="primary"
                                                                size="small"
                                                                onClick={() => handlePhoneClick(donor.mobileNumber)}
                                                            >
                                                                <Phone fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={donor.email ? "Send Email" : "No email available"}>
                                                            <span>
                                                                <IconButton
                                                                    color="primary"
                                                                    size="small"
                                                                    disabled={!donor.email}
                                                                    onClick={() => donor.email && handleEmailClick(donor.email)}
                                                                >
                                                                    <Email fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                                                <Typography variant="h6" color="text.secondary">
                                                    No available donors found
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" mt={1}>
                                                    Try adjusting your filters
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            component="div"
                            count={filteredDonors.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Box>

                    {/* Contact Dialog */}
                    <Dialog
                        open={contactDialogOpen}
                        onClose={handleCloseDialog}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>
                            <Typography variant="h6" fontWeight={600}>
                                Contact Donor
                            </Typography>
                        </DialogTitle>
                        <DialogContent>
                            {selectedDonor && (
                                <Box sx={{ py: 2 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Typography variant="h5" fontWeight={600} gutterBottom>
                                                {selectedDonor.fullName}
                                            </Typography>
                                            <Box display="flex" gap={1} mb={2}>
                                                <Chip
                                                    label={selectedDonor.bloodGroup?.value}
                                                    color="error"
                                                    size="small"
                                                />
                                                <Chip
                                                    label={selectedDonor.city?.value}
                                                    color="primary"
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box
                                                display="flex"
                                                gap={2}
                                                justifyContent="center"
                                                mt={2}
                                            >
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    size="large"
                                                    startIcon={<Phone />}
                                                    onClick={() => {
                                                        handlePhoneClick(selectedDonor.mobileNumber);
                                                        handleCloseDialog();
                                                    }}
                                                    sx={{
                                                        minWidth: 150,
                                                        py: 1.5
                                                    }}
                                                >
                                                    Call
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    color="success"
                                                    size="large"
                                                    startIcon={<Email />}
                                                    disabled={!selectedDonor.email}
                                                    onClick={() => {
                                                        if (selectedDonor.email) {
                                                            handleEmailClick(selectedDonor.email);
                                                            handleCloseDialog();
                                                        }
                                                    }}
                                                    sx={{
                                                        minWidth: 150,
                                                        py: 1.5
                                                    }}
                                                >
                                                    Email
                                                </Button>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box mt={2}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Mobile: {selectedDonor.mobileNumber}
                                                </Typography>
                                                {selectedDonor.email && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Email: {selectedDonor.email}
                                                    </Typography>
                                                )}
                                                {selectedDonor.lastDonationDate && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Last Donation: {selectedDonor.lastDonationDate}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDialog}>Close</Button>
                        </DialogActions>
                    </Dialog>
                </Container>
            </Box>
        </MainLayout>
    );
}

export default Donors;
